import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { AuthUser } from '../lib/auth';
import { AkorepsWordmark } from './AkorepsWordmark';
import { CloseIcon, PlusIcon } from './icons';
import { useScrollLock } from '../hooks/useScrollLock';
import { useAuthOrgs } from '../hooks/useAuthOrgs';

interface MobileMenuDrawerProps {
  open: boolean;
  onClose: () => void;
  user: AuthUser | null;
  isAdmin: boolean;
  canViewTeam?: boolean;
  onLogout: () => void;
}

export function MobileMenuDrawer({
  open,
  onClose,
  user,
  isAdmin,
  canViewTeam = false,
  onLogout,
}: MobileMenuDrawerProps) {
  const location = useLocation();
  const { orgs, current, hasMultiple, switchOrg } = useAuthOrgs();
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState<'in' | 'out' | null>(null);
  const visibleRef = useRef(false);
  visibleRef.current = visible;

  useScrollLock(visible);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setAnimating('in');
    } else if (visibleRef.current) {
      setAnimating('out');
    }
  }, [open]);

  const handleAnimationEnd = () => {
    if (animating === 'out') {
      setVisible(false);
      setAnimating(null);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const linkClass = (path: string) =>
    `block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
      isActive(path) ? 'bg-green-50 text-green-800' : 'text-gray-700 hover:bg-gray-50'
    }`;

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-50 md:hidden transition-opacity ${
          animating === 'out' ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ transitionDuration: '200ms' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-72 bg-white z-50 shadow-xl md:hidden flex flex-col ${
          animating === 'out' ? 'animate-slide-out-right' : 'animate-slide-in-right'
        }`}
        onAnimationEnd={handleAnimationEnd}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-gray-100">
          <span className="sr-only">Akoreps</span>
          <AkorepsWordmark className="h-7 w-auto" aria-hidden />
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-gray-500 hover:text-gray-700"
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        {/* User info */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-900 truncate">{user?.name || user?.email}</p>
          {user?.name && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
        </div>

        {current && (
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Workspace
            </p>
            <p className="text-sm font-medium text-gray-900 truncate" title={current.orgName}>
              {current.orgName}
            </p>
            {hasMultiple && (
              <div className="mt-2 space-y-1">
                {orgs.map((org) => (
                  <button
                    key={org.orgId}
                    type="button"
                    onClick={() => {
                      if (!org.isCurrent) void switchOrg(org.orgId);
                      onClose();
                    }}
                    className={`w-full text-left text-sm px-3 py-2 rounded-md flex items-center justify-between gap-2 ${
                      org.isCurrent
                        ? 'bg-green-50 text-green-800 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="min-w-0 truncate">{org.orgName}</span>
                    {org.isCurrent ? (
                      <span className="text-green-600 shrink-0 text-xs" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Nav links */}
        <div className="flex-1 overflow-y-auto py-2 px-2">
          <div className="space-y-1">
            <Link to="/profile" className={linkClass('/profile')}>
              Profile
            </Link>
            <Link to="/" className={linkClass('/')}>
              Dashboard
            </Link>
            <Link to="/practice" className={linkClass('/practice')}>
              Practice Sessions
            </Link>
            <Link to="/practice/new" className={linkClass('/practice/new')}>
              <span className="flex items-center gap-2">
                <PlusIcon className="w-5 h-5 text-green-600" />
                New Session
              </span>
            </Link>
            <Link to="/personas" className={linkClass('/personas')}>
              Personas
            </Link>
            {canViewTeam && (
              <Link to="/team" className={linkClass('/team')}>
                Team
              </Link>
            )}
          </div>

          {isAdmin && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Admin
              </p>
              <div className="space-y-1">
                <Link to="/onboarding" className={linkClass('/onboarding')}>
                  Business Profile
                </Link>
                <Link to="/goals" className={linkClass('/goals')}>
                  Goals
                </Link>
                <Link to="/settings" className={linkClass('/settings')}>
                  Settings
                </Link>
              </div>
            </div>
          )}

          {user?.isPlatformAdmin && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="px-4 text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">
                Platform
              </p>
              <div className="space-y-1">
                <Link
                  to="/admin"
                  className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                    isActive('/admin') ? 'bg-red-50 text-red-800' : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  Admin Panel
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-4">
          <button
            onClick={onLogout}
            className="w-full text-left px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    </>
  );
}
